import React, { useState } from 'react';
import { HandHeart, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { api } from '../lib/api';
import { PageHeader } from '../components/Shared';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { toast } from 'sonner';

const INTERESTS = ['Door knocking', 'Sign locations', 'Phone calls', 'Events & setup', 'Social media sharing', 'Delivering flyers', 'Data & research', 'Driving on election day'];

export default function Volunteer() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', interests: [], availability: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const toggleInterest = (i) => setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setBusy(true);
    try {
      await api.post('/volunteers', form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader pageKey="volunteer" kicker="JOIN THE TEAM" title="Volunteer for Steve" subtitle="Signs, doors, calls, events. Every bit of help brings accountability closer to Ward 8." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {done ? (
          <Card>
            <CardContent className="p-8 text-center" data-testid="volunteer-success">
              <CheckCircle2 className="h-14 w-14 text-emerald-600 mx-auto mb-4" />
              <p className="font-serif font-black text-[#1a2744] text-2xl">Thank you!</p>
              <p className="text-slate-600 mt-2">The campaign team will contact you soon. Welcome aboard.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-5 sm:p-8">
              <form onSubmit={submit} className="space-y-5" data-testid="volunteer-form">
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Your name <span className="text-red-600">*</span></label>
                  <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="h-12" data-testid="volunteer-name-input" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-bold text-[#1a2744] mb-2">Email <span className="text-red-600">*</span></label>
                    <Input type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="h-12" data-testid="volunteer-email-input" />
                  </div>
                  <div>
                    <label className="block font-bold text-[#1a2744] mb-2">Phone</label>
                    <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="h-12" data-testid="volunteer-phone-input" />
                  </div>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">How would you like to help?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {INTERESTS.map((i) => (
                      <label key={i} className={`flex items-center gap-2.5 border rounded-xl px-3.5 py-3 cursor-pointer text-sm font-semibold ${form.interests.includes(i) ? 'border-[#1a2744] bg-slate-50' : 'border-slate-200'}`}>
                        <Checkbox checked={form.interests.includes(i)} onCheckedChange={() => toggleInterest(i)} data-testid={`volunteer-interest-${i.toLowerCase().replace(/[^a-z]+/g, '-')}`} />
                        {i}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Availability</label>
                  <Input value={form.availability} onChange={(e) => setForm((f) => ({ ...f, availability: e.target.value }))} placeholder="e.g. Weekends, weekday evenings" className="h-12" data-testid="volunteer-availability-input" />
                </div>
                <div>
                  <label className="block font-bold text-[#1a2744] mb-2">Anything else?</label>
                  <Textarea value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))} rows={4} data-testid="volunteer-message-input" />
                </div>
                <Button type="submit" disabled={busy} className="w-full h-13 py-3.5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold text-base btn-press" data-testid="volunteer-submit-button">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <HandHeart className="h-5 w-5 mr-2" />} Sign Me Up
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        {/* Steve's 2-page flyer for volunteers to print and share by hand */}
        <div className="mt-6" data-testid="volunteer-flyer-section">
          <FlyerDownloadSection compact loc="volunteer" />
        </div>
      </div>
    </div>
  );
}
